/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  duplicateElement,
  elementIs,
} from '@web-stories-wp/story-editor/src/elements';
import { v4 as uuidv4 } from 'uuid';
import { produce } from 'immer';
import generateGroupName from '@src/web-stories-wp/story-editor/src/utils/generateGroupName';
import { remapTextBoxAttachment } from '../../../../utils/textBoxAttachment';

/**
 * Internal dependencies
 */
import { duplicateGroup } from './duplicateGroup';
// import type { ReducerState, ReducerStateDraft } from '../../../../types';

type DuplicateElementsByIdProps = {
  elementIds: string[];
  pasteInPlace?: boolean;
};

/**
 * Duplicate all elements specified by `elementIds` on the current page.
 * Set selected elements to be the newly created elements.
 *
 * If given `elementIds` are not a list, do nothing.
 */
export const duplicateElementsById = (
  draft: unknown, // @todo import ReducerStateDraft
  { elementIds, pasteInPlace }: DuplicateElementsByIdProps
) => {
  // @ts-ignore
  const page = draft.pages.find(({ id }) => id === draft.current);

  if (!page) {
    return;
  }

  let groupIdToDuplicate: string | null = null;

  for (const elementId of elementIds) {
    const elementToDuplicate = page.elements.find(({ id }) => id === elementId);

    if (!elementToDuplicate) {
      return;
    }

    // If one element is not in the group, we can't duplicate the group
    if (!elementToDuplicate.groupId) {
      groupIdToDuplicate = null;
      break;
    }

    if (groupIdToDuplicate === null) {
      groupIdToDuplicate = elementToDuplicate.groupId;

      // If any element is in a different group, we can't duplicate the group
    } else if (groupIdToDuplicate !== elementToDuplicate.groupId) {
      groupIdToDuplicate = null;
      break;
    }
  }

  if (groupIdToDuplicate) {
    const elementsInGroup = page.elements.filter(
      ({ groupId }) => groupId === groupIdToDuplicate
    );
    const allElementsToDuplicateAreInGroup = elementsInGroup.every(({ id }) =>
      elementIds.includes(id)
    );

    if (allElementsToDuplicateAreInGroup) {
      const newGroupId = uuidv4();
      const oldGroup = page.groups[groupIdToDuplicate];

      return duplicateGroup(draft, {
        oldGroupId: groupIdToDuplicate,
        groupId: newGroupId,
        name: generateGroupName(page.groups, oldGroup?.name ?? ''),
        isLocked: oldGroup?.isLocked ?? false,
      });
    }
  }

  let hasDeletedSomething = false;
  const elementIdTransferMap = Object.fromEntries(
    elementIds.map((elementId) => [elementId, uuidv4()])
  );

  for (const elementId of elementIds) {
    const elementIndex = page.elements.findIndex(({ id }) => id === elementId);

    if (elementIndex < 0) {
      return;
    }

    const elementToDuplicate = page.elements[elementIndex];

    if (
      elementIs.backgroundable(elementToDuplicate) &&
      elementToDuplicate.isBackground
    ) {
      return;
    }

    const { element: rawElement, elementAnimations } = duplicateElement({
      element: elementToDuplicate,
      animations: page.animations,
      currentElements: page?.elements,
      pasteInPlace,
      newId: elementIdTransferMap[elementId],
    });
    const element = remapTextBoxAttachment(rawElement, elementIdTransferMap);

    if (!hasDeletedSomething) {
      hasDeletedSomething = true;
      // @ts-ignore
      draft.selection = [];
    }

    // @ts-ignore
    draft.selection.push(element.id);

    if (page.animations) {
      page.animations = page.animations.concat(elementAnimations);
    }
    page.elements.splice(elementIndex + 1, 0, element);
  }
};

// export default produce<ReducerState, [DuplicateElementsByIdProps]>(
//   duplicateElementsById
// );

export default produce(duplicateElementsById);
